import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Box, Grid, CircularProgress } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { stripeOnboardingComplete } from 'src/apiCalls';
import logger from 'src/logger';
export default function StripeReturnPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { user } = useUserContext();
  const accountId = searchParams.get('account_id');
  const userId = searchParams.get('user_id');

  useEffect(() => {
    // Function to handle onboarding completion
    const completeOnboarding = async () => {
      try {
        if (!user || !user.accessToken) {
          setError('User is not authenticated or access token is missing.');
          setLoading(false);
          return;
        }

        // Ensure both accountId and userId are available before proceeding
        if (accountId && userId) {
          // Call backend API to mark onboarding as complete
          logger.info(`acces token ${user.accessToken}, ${user.uid}, ${accountId}`);
          const response = await stripeOnboardingComplete(user.accessToken, user.uid, accountId);
          
          // Check for successful response from API
          if (response.statusCode === 200) {
            setSuccess(true);
          } else {
            logger.info(response);
            setError('Failed to complete Stripe onboarding.');
          }
        } else {
          setError('Invalid return URL. Missing account or user information.');
        }
      } catch (err) {
        setError('An error occurred during onboarding completion.');
      } finally {
        setLoading(false);
      }
    };

    // Wait for user to be available before making API call
    if (user && user.accessToken) {
      completeOnboarding();
    } else {
      setError('User access token is not available.');
      setLoading(false);
    }
  }, [accountId, userId, user]);

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h5" color="error" align="center" sx={{ mt: 5 }}>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100vh',
        backgroundImage: 'url(/static/images/overview/onlyfishbackground2.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        textAlign: 'center',
        pt: 10,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: '100%',
          mx: 2,
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom>
          Stripe Account Linked Successfully!
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          You're all set to start accepting payments. Now you can upload your catch reports and start earning!
        </Typography>

        {/* Action buttons */}
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={RouterLink}
              to="/images/addsingle"
            >
              Create Catch Report
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              component={RouterLink}
              to="/accounts/dashboard"
            >
              View Your Account
            </Button>
          </Grid>
        </Grid>

        {/* Explanation section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            What’s Next?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You can now upload images of your catch reports and let fellow anglers buy the locations.
            Your connected Stripe account will handle the payments, and you'll earn money for sharing
            your top fishing spots!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enjoy the platform, and happy fishing!
          </Typography>
        </Box>
      </Container>

      {/* Background overlay for better contrast */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        }}
      />
    </Box>
  );
}
